(function () {
  'use strict';

  const DIGITISATION_FEE_VARIANT_ID = 55420858270081;
  const DIGITISATION_FEE_VARIANT_ID_STR = String(DIGITISATION_FEE_VARIANT_ID);
  const CART_PRICING_SYNCED_EVENT = 'new-logo:cart-pricing-synced';
  const CART_PRICING_SOURCE = 'new-logo-cart-pricing';

  function emitCartPricingSynced(reason) {
    document.dispatchEvent(
      new CustomEvent(CART_PRICING_SYNCED_EVENT, {
        detail: {
          reason: String(reason || ''),
          at: Date.now(),
        },
      })
    );
  }

  function toWholeNumber(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function toCents(value) {
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return 0;
      if (Number.isInteger(value)) return value;
      return Math.round(value * 100);
    }

    const raw = String(value ?? '').trim();
    if (!raw) return 0;

    // Plain digits are treated as cents (Shopify cart.js style)
    if (/^-?\d+$/.test(raw)) {
      const parsedInteger = Number.parseInt(raw, 10);
      return Number.isFinite(parsedInteger) ? parsedInteger : 0;
    }

    // Currency/decimal strings are treated as major units
    const normalized = raw.replace(/[^0-9,.-]/g, '').replace(/,/g, '');
    const parsedDecimal = Number.parseFloat(normalized);
    if (!Number.isFinite(parsedDecimal)) return 0;
    return Math.round(parsedDecimal * 100);
  }

  function resolveVatRatePercent() {
    const toggle = document.querySelector('[data-vat-toggle]');
    if (!toggle) return null;
    const parsed = Number.parseFloat(String(toggle.dataset.vatRate || '').trim());
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return parsed;
  }

  function normaliseInclusiveCostCents(cents) {
    const value = toCents(cents);
    if (value <= 0) return 0;

    const vatRatePercent = resolveVatRatePercent();
    if (vatRatePercent == null) return value;

    const multiplier = 1 + vatRatePercent / 100;
    if (!Number.isFinite(multiplier) || multiplier <= 0) return value;

    return Math.round(value / multiplier);
  }

  function formatMoney(cents) {
    const safeCents = Math.round(Number(cents) || 0);
    const themeFormat = window.FoxTheme?.settings?.moneyFormat;
    const formatter = window.FoxTheme?.utils?.formatMoney;
    if (typeof formatter === 'function' && themeFormat) {
      try {
        const formatted = String(formatter(safeCents, themeFormat) || '').trim();
        // Guard against malformed strings like "£ 133.24.64".
        const malformed = /[£$€]\s?\d[\d,]*\.\d{2}\.\d+/.test(formatted);
        if (formatted && !malformed) {
          return formatted;
        }
      } catch (error) {
        // fall through to safe formatter
      }
    }

    const amount = safeCents / 100;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function getLineFromItemElement(itemEl) {
    const id = String(itemEl?.id || '');
    const match = id.match(/-(\d+)$/);
    if (match) {
      const line = toWholeNumber(match[1]);
      if (line > 0) return line;
    }

    const indexInput = itemEl?.querySelector('[data-index]');
    const line = toWholeNumber(indexInput?.getAttribute('data-index') || '');
    return line > 0 ? line : null;
  }

  function buildCartItemMapByElement(itemElements, cartItems) {
    const elements = Array.isArray(itemElements) ? itemElements : [];
    const items = Array.isArray(cartItems) ? cartItems : [];
    const byKey = new Map();
    const byLine = new Map();
    const usedItemIndexes = new Set();
    const elementToItem = new Map();

    items.forEach((item, index) => {
      const key = String(item?.key || '').trim();
      if (!key || byKey.has(key)) return;
      byKey.set(key, { item, index });
    });
    items.forEach((item, index) => {
      const line = index + 1;
      if (line <= 0 || byLine.has(line)) return;
      byLine.set(line, { item, index });
    });

    // Prefer explicit key mapping if available.
    elements.forEach((itemEl) => {
      const key = String(itemEl?.getAttribute('data-cart-item-key') || '').trim();
      if (!key) return;
      const matched = byKey.get(key);
      if (!matched) return;
      if (usedItemIndexes.has(matched.index)) return;
      usedItemIndexes.add(matched.index);
      elementToItem.set(itemEl, matched);
    });

    // Next prefer explicit line index mapping (CartDrawer-Item-<line> / CartItem-<line>)
    // so hidden/auxiliary rows never shift regular item pricing.
    elements.forEach((itemEl) => {
      if (elementToItem.has(itemEl)) return;
      const line = getLineFromItemElement(itemEl);
      if (!line || line <= 0) return;
      const matched = byLine.get(line);
      if (!matched) return;
      if (usedItemIndexes.has(matched.index)) return;
      usedItemIndexes.add(matched.index);
      elementToItem.set(itemEl, matched);
    });

    const remainingElements = elements.filter((itemEl) => !elementToItem.has(itemEl));
    const remainingItems = items
      .map((item, index) => ({ item, index }))
      .filter((entry) => !usedItemIndexes.has(entry.index));

    const regularElements = [];
    const feeElements = [];
    remainingElements.forEach((itemEl) => {
      const variantId = String(itemEl?.getAttribute('data-variant-id') || '').trim();
      if (variantId === DIGITISATION_FEE_VARIANT_ID_STR) {
        feeElements.push(itemEl);
      } else {
        regularElements.push(itemEl);
      }
    });

    const regularItems = [];
    const feeItems = [];
    remainingItems.forEach((entry) => {
      if (isDigitisationFeeItem(entry.item)) {
        feeItems.push(entry);
      } else {
        regularItems.push(entry);
      }
    });

    regularElements.forEach((itemEl, index) => {
      const matched = regularItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    feeElements.forEach((itemEl, index) => {
      const matched = feeItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    return elementToItem;
  }

  function setInlinePriceText(priceElement, moneyText) {
    if (!priceElement) return;

    const regularPrice = priceElement.querySelector('.price__regular');
    if (regularPrice) {
      regularPrice.textContent = moneyText;
      return;
    }

    const preserveSelectors = ['.unit-price', '.visually-hidden', '.price__sale'];
    const preserveNodes = Array.from(priceElement.children).filter((child) =>
      preserveSelectors.some((selector) => child.matches(selector))
    );

    // Remove any pre-existing money wrappers/text fragments to avoid leftovers like ".64".
    Array.from(priceElement.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && preserveNodes.includes(node)) return;
      priceElement.removeChild(node);
    });

    if (!preserveNodes.length) {
      priceElement.textContent = moneyText;
      return;
    }

    const firstPreserveNode = preserveNodes[0];
    const inlineMoney = document.createElement('span');
    inlineMoney.className = 'new-logo-inline-price';
    inlineMoney.textContent = moneyText;
    priceElement.insertBefore(inlineMoney, firstPreserveNode);
  }

  function getVisibleCartItemCount(items) {
    return (Array.isArray(items) ? items : []).reduce((sum, item) => {
      if (isDigitisationFeeItem(item)) return sum;
      const qty = toWholeNumber(item?.quantity);
      return sum + (qty > 0 ? qty : 0);
    }, 0);
  }

  function syncCartDrawerEmptyState(cart) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const items = Array.isArray(cart?.items) ? cart.items : [];
    const visibleItemCount = getVisibleCartItemCount(items);
    const isEmpty = visibleItemCount <= 0;

    drawer.querySelectorAll('[id^="CartDrawerEmpty-"]').forEach((el) => {
      el.classList.toggle('hidden', !isEmpty);
    });
    drawer.querySelectorAll('[id^="CartDrawerBody-"]').forEach((el) => {
      el.classList.toggle('hidden', isEmpty);
    });
    drawer.querySelectorAll('[id^="CartDrawerFooter-"]').forEach((el) => {
      el.classList.toggle('hidden', isEmpty);
    });

    drawer.querySelectorAll('cart-count[data-type="blank"]').forEach((countEl) => {
      if (isEmpty) {
        countEl.setAttribute('hidden', '');
        countEl.textContent = '(0)';
        return;
      }

      const displayCount = visibleItemCount < 100 ? String(visibleItemCount) : '99+';
      countEl.removeAttribute('hidden');
      countEl.textContent = `(${displayCount})`;
    });
  }

  function resolveCustomisationNoteAnchor(itemEl, pricesElement) {
    const lineTotalElement = itemEl?.querySelector('.cart-item__total .font-body-bolder');
    if (lineTotalElement && lineTotalElement.parentElement) {
      return { parent: lineTotalElement.parentElement, before: lineTotalElement };
    }

    const priceElement = pricesElement?.querySelector('.price');
    if (priceElement) {
      const wrapper = priceElement.closest('div');
      if (wrapper && wrapper.parentElement === pricesElement) {
        return { parent: pricesElement, before: wrapper };
      }
      if (priceElement.parentElement) {
        return { parent: priceElement.parentElement, before: priceElement };
      }
    }

    if (pricesElement) {
      return { parent: pricesElement, before: pricesElement.firstChild };
    }

    return null;
  }

  function upsertCustomisationNote(itemEl, pricesElement, extraPerItem) {
    if (!pricesElement) return;

    let note = pricesElement.querySelector('.new-logo-extra-note');
    const hasCost = extraPerItem > 0;

    if (!hasCost) {
      if (note) note.remove();
      return;
    }

    if (!note) {
      note = document.createElement('div');
      note.className = 'new-logo-extra-note text-subtext';
      note.style.fontSize = '1.04rem';
      note.style.lineHeight = '1.1';
      note.style.display = 'block';
      note.style.textAlign = 'right';
      note.style.width = '100%';
    }

    note.style.marginTop = '-2rem';
    note.style.marginBottom = '0.3rem';

    const anchor = resolveCustomisationNoteAnchor(itemEl, pricesElement);
    if (!anchor?.parent) return;

    if (note.parentElement !== anchor.parent) {
      if (note.parentElement) note.parentElement.removeChild(note);
      if (anchor.before) {
        anchor.parent.insertBefore(note, anchor.before);
      } else {
        anchor.parent.appendChild(note);
      }
    } else if (anchor.before && note.nextSibling !== anchor.before) {
      anchor.parent.insertBefore(note, anchor.before);
    }

    note.textContent = formatMoney(extraPerItem);
  }

  function getLogoCostParts(properties) {
    const source = properties || {};
    const rawBasis = String(source._logo_price_basis || '').trim().toLowerCase();
    const hasExplicitBasis = rawBasis === 'inclusive' || rawBasis === 'exclusive';
    const isInclusiveBasis = hasExplicitBasis ? rawBasis === 'inclusive' : true;
    let extraPerItem = toCents(source._logo_extra_per_item_cents);
    if (extraPerItem <= 0) {
      const positionsCount = toWholeNumber(source._logo_positions_count);
      const unitCents = toCents(source._logo_price_unit_cents);
      extraPerItem = positionsCount * unitCents;
    }

    let setupFee = toCents(source._logo_setup_fee_cents);
    if (setupFee <= 0) {
      const setupRequiredValue = String(source._logo_setup_required || source['Logo Setup Required'] || '').toLowerCase();
      const setupRequired = setupRequiredValue === 'yes' || setupRequiredValue === 'true' || setupRequiredValue === '1';
      const method = String(source._logo_method || source['Logo Method'] || '').toLowerCase();
      if (setupRequired && method === 'embroidery') {
        setupFee = toCents(window.NewLogoConfig?.prices?.digitalisationCents) || 1800;
      }
    }

    if (isInclusiveBasis) {
      extraPerItem = normaliseInclusiveCostCents(extraPerItem);
      setupFee = normaliseInclusiveCostCents(setupFee);
    }

    return {
      extraPerItem,
      setupFee,
    };
  }

  function isDigitisationFeeItem(item) {
    if (!item) return false;
    const variantId = String(item.variant_id ?? item.id ?? '').trim();
    return variantId === DIGITISATION_FEE_VARIANT_ID_STR;
  }

  function resolveLegacyLogoIdentity(properties) {
    const source = properties || {};
    const method = String(source._logo_method || source['Logo Method'] || '').trim().toLowerCase();
    const sizeTier = String(source._logo_size_tier || source['Logo Size'] || '').trim().toLowerCase();
    if (!method) return '';

    const artworkUrls = String(source._logo_artwork_urls || '')
      .split('|')
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    if (!artworkUrls.length) {
      [source._logo_artwork_url, source._logo_artwork, source._logo_artwork_2, source._logo_artwork_3]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean)
        .forEach((value) => artworkUrls.push(value));
    }
    const artworkNames = String(source._logo_artwork_filenames || source._logo_artwork_filename || '')
      .split('|')
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    const artworkSignature = artworkUrls.join('|') || artworkNames.join('|');
    const signature = `${artworkSignature}|${method}|${sizeTier}`.trim();
    return signature ? `legacy-logo:${signature}` : '';
  }

  function isSetupFeeRequiredForItem(item, index) {
    if (!item || isDigitisationFeeItem(item)) return null;
    const properties = item.properties || {};
    const logoType = String(properties._logo_type || properties['Logo Type'] || '').trim().toLowerCase();
    if (logoType && logoType !== 'logo') return null;
    const setupRequiredValue = String(properties._logo_setup_required || properties['Logo Setup Required'] || '').toLowerCase();
    const setupRequired = setupRequiredValue === 'yes' || setupRequiredValue === 'true' || setupRequiredValue === '1';
    const explicitGroupSetupFee = toCents(properties._logo_setup_group_fee_cents);
    const hasSetupSignal = setupRequired || explicitGroupSetupFee > 0;
    if (!hasSetupSignal) return null;

    const explicitSetupFee = toCents(properties._logo_setup_fee_cents);
    const method = String(properties._logo_method || properties['Logo Method'] || '').trim().toLowerCase();
    const fallbackSetupFee = setupRequired && method === 'embroidery' ? toCents(window.NewLogoConfig?.prices?.digitalisationCents) || 1800 : 0;
    const effectiveSetupFee = explicitGroupSetupFee > 0 ? explicitGroupSetupFee : explicitSetupFee > 0 ? explicitSetupFee : fallbackSetupFee;
    if (effectiveSetupFee <= 0) return null;
    const explicitSetupUnits = toWholeNumber(properties._logo_setup_units);
    const setupUnits = explicitSetupUnits > 0 ? explicitSetupUnits : 1;

    const explicitToken = String(properties._logo_customiser_token || '').trim();
    if (explicitToken) return { token: explicitToken, units: setupUnits };

    const legacyIdentity = resolveLegacyLogoIdentity(properties);
    if (legacyIdentity) return { token: legacyIdentity, units: setupUnits };

    const lineKey = String(item.key || '').trim();
    if (lineKey) return { token: lineKey, units: setupUnits };

    const groupToken = String(properties._logo_customisation_group || '').trim();
    return { token: groupToken || `line-${index}`, units: setupUnits };
  }

  function getRequiredDigitisationFeeQuantity(items) {
    const setupGroups = new Map();
    (items || []).forEach((item, index) => {
      const result = isSetupFeeRequiredForItem(item, index);
      if (!result || !result.token) return;
      const units = Number.parseInt(result.units, 10) || 1;
      const previous = setupGroups.get(result.token) || 0;
      setupGroups.set(result.token, Math.max(previous, units));
    });
    return Array.from(setupGroups.values()).reduce((sum, units) => sum + units, 0);
  }

  function getDigitisationFeeLineQuantity(items) {
    return (items || []).reduce((sum, item) => {
      if (!isDigitisationFeeItem(item)) return sum;
      return sum + toWholeNumber(item.quantity);
    }, 0);
  }

  function setControlLockState(element, shouldLock) {
    if (!element) return;
    const hasOriginalState = Object.prototype.hasOwnProperty.call(element.dataset, 'digitisationOriginalDisabled');
    if (!shouldLock && !hasOriginalState) return;

    if (!hasOriginalState) {
      element.dataset.digitisationOriginalDisabled = element.disabled ? 'true' : 'false';
    }

    if (shouldLock) {
      element.disabled = true;
      element.setAttribute('aria-disabled', 'true');
      return;
    }

    const originalDisabled = element.dataset.digitisationOriginalDisabled === 'true';
    element.disabled = originalDisabled;
    if (!originalDisabled) {
      element.removeAttribute('aria-disabled');
    }
    delete element.dataset.digitisationOriginalDisabled;
  }

  function setAnchorLockState(anchor, shouldLock) {
    if (!anchor) return;
    const hasOriginalState = Object.prototype.hasOwnProperty.call(anchor.dataset, 'digitisationOriginalTabindex');
    if (!shouldLock && !hasOriginalState) return;

    if (!hasOriginalState) {
      anchor.dataset.digitisationOriginalTabindex = anchor.hasAttribute('tabindex')
        ? anchor.getAttribute('tabindex')
        : '__none__';
      anchor.dataset.digitisationOriginalPointerEvents = anchor.style.pointerEvents || '';
      anchor.dataset.digitisationOriginalOpacity = anchor.style.opacity || '';
    }

    if (shouldLock) {
      anchor.setAttribute('aria-disabled', 'true');
      anchor.setAttribute('tabindex', '-1');
      anchor.style.pointerEvents = 'none';
      anchor.style.opacity = '0.45';
      return;
    }

    const originalTabindex = anchor.dataset.digitisationOriginalTabindex;
    if (originalTabindex === '__none__') {
      anchor.removeAttribute('tabindex');
    } else {
      anchor.setAttribute('tabindex', originalTabindex);
    }

    anchor.style.pointerEvents = anchor.dataset.digitisationOriginalPointerEvents || '';
    anchor.style.opacity = anchor.dataset.digitisationOriginalOpacity || '';
    anchor.removeAttribute('aria-disabled');
    delete anchor.dataset.digitisationOriginalTabindex;
    delete anchor.dataset.digitisationOriginalPointerEvents;
    delete anchor.dataset.digitisationOriginalOpacity;
  }

  function setDigitisationLineLockedState(itemEl, shouldLock) {
    if (!itemEl) return;
    if (shouldLock) {
      itemEl.dataset.digitisationFeeLocked = 'true';
    } else {
      delete itemEl.dataset.digitisationFeeLocked;
    }

    const quantityControls = itemEl.querySelectorAll('.quantity__button, .quantity__input, input[name="updates[]"]');
    quantityControls.forEach((control) => setControlLockState(control, shouldLock));

    const removeLinks = itemEl.querySelectorAll('.cart-item__remove, [is="cart-remove-item"], a.btn-remove');
    removeLinks.forEach((anchor) => setAnchorLockState(anchor, shouldLock));
  }

  function applyDigitisationLineLocks(cart, requiredQty) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const itemElements = Array.from(document.querySelectorAll('.cart-item'));
    const elementMap = buildCartItemMapByElement(itemElements, items);

    itemElements.forEach((itemEl) => {
      const matched = elementMap.get(itemEl) || null;
      const cartItem = matched?.item || null;
      const shouldLock = requiredQty > 0 && isDigitisationFeeItem(cartItem);
      setDigitisationLineLockedState(itemEl, shouldLock);
      if (cartItem?.key) {
        itemEl.setAttribute('data-cart-item-key', String(cartItem.key));
      }
    });
  }

  function clearSetupFeeRows() {
    document.querySelectorAll('.new-logo-setup-row').forEach((row) => row.remove());
  }

  function notifyCartStateUpdated(cart) {
    if (!cart || typeof cart !== 'object') return;

    if (
      window.FoxTheme &&
      window.FoxTheme.pubsub &&
      window.FoxTheme.pubsub.publish &&
      window.FoxTheme.pubsub.PUB_SUB_EVENTS &&
      window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
    ) {
      window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: CART_PRICING_SOURCE,
        cart,
      });
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart,
          source: CART_PRICING_SOURCE,
        },
      })
    );
  }

  async function addDigitisationFeeVariant(quantity) {
    if (quantity <= 0) return;
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: DIGITISATION_FEE_VARIANT_ID,
            quantity,
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to add digitalisation fee item.');
    }
  }

  async function setDigitisationFeeVariantQuantity(quantity) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: {
          [DIGITISATION_FEE_VARIANT_ID]: Math.max(0, toWholeNumber(quantity)),
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to update digitalisation fee item.');
    }
  }

  let syncPromise = null;
  async function reconcileDigitisationFeeLine(cart) {
    let workingCart = cart;
    let safetyCounter = 0;

    while (safetyCounter < 6) {
      safetyCounter += 1;

      const items = Array.isArray(workingCart?.items) ? workingCart.items : [];
      const requiredQty = getRequiredDigitisationFeeQuantity(items);
      const currentQty = getDigitisationFeeLineQuantity(items);

      if (requiredQty === currentQty) {
        applyDigitisationLineLocks(workingCart, requiredQty);
        return workingCart;
      }

      // If another reconcile is active, wait, refetch, and re-evaluate against latest cart.
      if (syncPromise) {
        await syncPromise;
        workingCart = await fetchCart();
        continue;
      }

      syncPromise = (async () => {
        if (requiredQty > currentQty) {
          await addDigitisationFeeVariant(requiredQty - currentQty);
        } else {
          await setDigitisationFeeVariantQuantity(requiredQty);
        }

        const refreshedCart = await fetchCart();
        applyDigitisationLineLocks(refreshedCart, requiredQty);

        document.dispatchEvent(
          new CustomEvent('new-logo:digitisation-fee-reconciled', {
            detail: {
              requiredQty,
              cart: refreshedCart,
            },
          })
        );
        notifyCartStateUpdated(refreshedCart);

        return refreshedCart;
      })();

      try {
        workingCart = await syncPromise;
      } finally {
        syncPromise = null;
      }
    }

    // Failsafe: return latest known server cart if reconciliation kept changing.
    const fallbackCart = await fetchCart();
    const fallbackRequiredQty = getRequiredDigitisationFeeQuantity(Array.isArray(fallbackCart?.items) ? fallbackCart.items : []);
    applyDigitisationLineLocks(fallbackCart, fallbackRequiredQty);
    return fallbackCart;
  }

  function applyCartDisplayPricing(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    syncCartDrawerEmptyState(cart);
    if (!items.length) {
      document.querySelectorAll('.new-logo-extra-note').forEach((el) => el.remove());
      clearSetupFeeRows();
      const emptyTotal = toCents(cart?.total_price);
      document.querySelectorAll('.totals__subtotal-value').forEach((subtotalElement) => {
        subtotalElement.textContent = formatMoney(emptyTotal);
      });
      return;
    }

    const requiredDigitisationFeeQty = getRequiredDigitisationFeeQuantity(items);
    applyDigitisationLineLocks(cart, requiredDigitisationFeeQty);

    const itemElements = Array.from(document.querySelectorAll('.cart-item'));
    const elementMap = buildCartItemMapByElement(itemElements, items);

    itemElements.forEach((itemEl) => {
      const matched = elementMap.get(itemEl) || null;
      const item = matched?.item || null;
      if (!item) return;
      if (item?.key) {
        itemEl.setAttribute('data-cart-item-key', String(item.key));
      }
      if (isDigitisationFeeItem(item)) return;
      const properties = item?.properties || {};
      const costParts = getLogoCostParts(properties);
      const extraPerItem = costParts.extraPerItem;
      const qty = toWholeNumber(item.quantity);
      const lineExtra = extraPerItem * qty;

      const displayFinalPrice = toCents(item.final_price) + extraPerItem;
      const displayOriginalPrice = toCents(item.original_price) + extraPerItem;
      const displayLinePrice = toCents(item.final_line_price) + lineExtra;

      const pricesElement = itemEl.querySelector('.cart-item__prices');
      const priceElement = pricesElement?.querySelector('.price');
      const salePriceElement = priceElement?.querySelector('s');
      if (salePriceElement) {
        salePriceElement.textContent = formatMoney(displayOriginalPrice);
      }
      setInlinePriceText(priceElement, formatMoney(displayFinalPrice));

      const lineTotalElement = itemEl.querySelector('.cart-item__total .font-body-bolder');
      if (lineTotalElement) {
        lineTotalElement.textContent = formatMoney(displayLinePrice);
      }

      upsertCustomisationNote(itemEl, pricesElement, extraPerItem);
    });

    const priceExtraTotal = items.reduce((sum, item) => {
      const properties = item?.properties || {};
      const costParts = getLogoCostParts(properties);
      const extraPerItem = costParts.extraPerItem;
      return sum + extraPerItem * toWholeNumber(item.quantity);
    }, 0);

    const displaySubtotal = toCents(cart.total_price) + priceExtraTotal;
    document.querySelectorAll('.totals__subtotal-value').forEach((subtotalElement) => {
      subtotalElement.textContent = formatMoney(displaySubtotal);
    });

    clearSetupFeeRows();
  }

  async function fetchCart() {
    const response = await fetch(`/cart.js?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart JSON');
    }

    return response.json();
  }

  let pendingTimer = null;
  let pendingCartPayload = null;
  let refreshInFlight = false;

  function getEventCartPayload(candidate) {
    if (!candidate || typeof candidate !== 'object') return null;
    if (!Array.isArray(candidate.items)) return null;
    return candidate;
  }

  async function runRefresh(cartHint = null) {
    if (refreshInFlight) {
      const queued = getEventCartPayload(cartHint);
      if (queued) pendingCartPayload = queued;
      return;
    }

    refreshInFlight = true;
    try {
      const safeHint = getEventCartPayload(cartHint);
      const cart = safeHint || (await fetchCart());
      applyCartDisplayPricing(cart);
      emitCartPricingSynced('completed-initial');

      const reconciledCart = await reconcileDigitisationFeeLine(cart);
      if (reconciledCart && reconciledCart !== cart) {
        applyCartDisplayPricing(reconciledCart);
      }

      emitCartPricingSynced('completed');
    } catch (error) {
      console.error(error);
      emitCartPricingSynced('error');
    } finally {
      refreshInFlight = false;
      const queued = pendingCartPayload;
      pendingCartPayload = null;
      if (queued) {
        runRefresh(queued);
      }
    }
  }

  function scheduleRefresh(delay = 80, cartHint = null) {
    const eventCart = getEventCartPayload(cartHint);
    if (eventCart) {
      pendingCartPayload = eventCart;
    }

    if (pendingTimer) clearTimeout(pendingTimer);
    if (delay <= 0) {
      const queued = pendingCartPayload;
      pendingCartPayload = null;
      runRefresh(queued);
      return;
    }

    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      const queued = pendingCartPayload;
      pendingCartPayload = null;
      runRefresh(queued);
    }, delay);
  }

  function init() {
    scheduleRefresh(0);

    document.addEventListener('cart:updated', (event) => {
      if (String(event?.detail?.source || '') === CART_PRICING_SOURCE) return;
      scheduleRefresh(0, event?.detail?.cart || null);
    });
    document.addEventListener('cart:refresh', () => {
      pendingCartPayload = null;
      scheduleRefresh(120);
    });

    document.addEventListener('logo-customiser:saved', () => scheduleRefresh(220));
    window.addEventListener('logo-customiser:saved', () => scheduleRefresh(220));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
